import { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import profileAPI from '../../services/profileApi';

const MAX_BYTES = 5 * 1024 * 1024;
const TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const ProfileAvatar = ({ name, hasAvatar, version, onChanged }) => {
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let url;
    let cancelled = false;
    if (!hasAvatar) {
      setSrc(null);
      return undefined;
    }
    profileAPI.avatarBlob()
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => setSrc(null));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [hasAvatar, version]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!TYPES.includes(file.type)) return toast.error('Please choose a JPG, PNG or WEBP image');
    if (file.size > MAX_BYTES) return toast.error('Image must be 5MB or smaller');
    setBusy(true);
    try {
      await profileAPI.uploadAvatar(file);
      toast.success('Profile photo updated');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    setBusy(true);
    try {
      await profileAPI.removeAvatar();
      toast.success('Profile photo removed');
      onChanged();
    } catch {
      toast.error('Could not remove photo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative group shrink-0" data-testid="profile-avatar">
      <div className="w-32 h-32 rounded-full overflow-hidden ring-4 ring-purple-500/30 shadow-lg bg-gradient-to-br from-purple-600 to-teal-500 flex items-center justify-center text-5xl font-bold text-white">
        {src
          ? <img src={src} alt={`${name} avatar`} className="w-full h-full object-cover" data-testid="profile-avatar-img" />
          : <span data-testid="profile-avatar-initial">{(name || '?').charAt(0).toUpperCase()}</span>}
        {busy && (
          <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-white" />
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="profile-avatar-input" />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        title="Upload photo"
        data-testid="profile-avatar-upload-btn"
        className="absolute bottom-0 right-0 p-3 bg-purple-600 rounded-full text-white hover:bg-purple-700 transition-transform shadow-lg group-hover:scale-110"
      >
        <Camera className="h-5 w-5" />
      </button>
      {hasAvatar && (
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          title="Remove photo"
          data-testid="profile-avatar-remove-btn"
          className="absolute top-0 right-0 p-2 bg-gray-900/90 border border-gray-700 rounded-full text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

export default ProfileAvatar;
